// src/components/permissions/PermissionForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { createPermission, updatePermission, fetchPermission, clearPermissionError, clearPermissionSuccess } from '../../store/actions/permissionActions';
import { fetchPermissionModules } from '../../store/actions/permissionActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';

const PermissionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedPermission, loading, error, success } = useSelector((state) => state.permissions);
  const { permissionModules } = useSelector((state) => state.permissions);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    module: '',
    action: '',
    resource: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [newModule, setNewModule] = useState('');
  const [showNewModuleInput, setShowNewModuleInput] = useState(false);

  // Styles object
  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.875rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
      marginBottom: '0.5rem',
    },
    subtitle: {
      fontSize: '0.875rem',
      color: '#6b7280',
      margin: 0,
    },
    
    // Form
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '2rem',
    },
    
    // Form fields
    fieldGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    required: {
      color: '#ef4444',
      marginLeft: '0.25rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem 0.875rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem 0.875rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
      minHeight: '100px',
      resize: 'vertical',
      boxSizing: 'border-box',
    },
    select: {
      width: '100%',
      padding: '0.625rem 2rem 0.625rem 0.875rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1.5em 1.5em',
    },
    inputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    inputError: {
      borderColor: '#ef4444',
    },
    inputErrorFocus: {
      borderColor: '#ef4444',
      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
    },
    errorMessage: {
      fontSize: '0.75rem',
      color: '#ef4444',
      marginTop: '0.25rem',
    },
    helperText: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
    
    // Action buttons row
    actionRow: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    actionButton: {
      flex: 1,
      padding: '0.625rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    actionButtonSelected: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
      borderColor: '#2563eb',
    },
    actionButtonHover: {
      backgroundColor: '#f9fafb',
    },
    
    // New module input
    newModuleContainer: {
      marginTop: '0.5rem',
      display: 'flex',
      gap: '0.5rem',
    },
    newModuleInput: {
      flex: 1,
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
    },
    addModuleButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#10b981',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    addModuleButtonHover: {
      backgroundColor: '#059669',
    },
    cancelModuleButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#6b7280',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    cancelModuleButtonHover: {
      backgroundColor: '#4b5563',
    },
    
    // Examples
    examplesContainer: {
      marginTop: '2rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
    },
    examplesTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '0.75rem',
    },
    examplesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '0.5rem',
    },
    exampleItem: {
      padding: '0.5rem',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    exampleItemHover: {
      backgroundColor: '#f3f4f6',
      borderColor: '#d1d5db',
    },
    exampleName: {
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#111827',
    },
    exampleDesc: {
      fontSize: '0.625rem',
      color: '#6b7280',
      marginTop: '0.125rem',
    },
    
    // Form actions
    formActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
      paddingTop: '1.5rem',
      borderTop: '1px solid #e5e7eb',
    },
    cancelButton: {
      padding: '0.625rem 1.25rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    submitButton: {
      padding: '0.625rem 1.25rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    
    // Loading overlay
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
  };

  // Common actions for examples
  const commonActions = ['create', 'read', 'update', 'delete', 'manage', 'view', 'export', 'import'];

  // Example permissions
  const examplePermissions = [
    { name: 'user:create', module: 'users', action: 'create', resource: 'user', description: 'Create new users' },
    { name: 'user:read', module: 'users', action: 'read', resource: 'user', description: 'View user details' },
    { name: 'user:update', module: 'users', action: 'update', resource: 'user', description: 'Update user information' },
    { name: 'user:delete', module: 'users', action: 'delete', resource: 'user', description: 'Delete users' },
    { name: 'role:manage', module: 'roles', action: 'manage', resource: 'role', description: 'Manage roles and permissions' },
    { name: 'report:export', module: 'reports', action: 'export', resource: 'report', description: 'Export reports' },
  ];

  useEffect(() => {
    dispatch(fetchPermissionModules());
    
    if (id) {
      dispatch(fetchPermission(id));
    } else {
      setInitialLoadComplete(true);
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && selectedPermission) {
      setFormData({
        name: selectedPermission.name || '',
        description: selectedPermission.description || '',
        module: selectedPermission.module || '',
        action: selectedPermission.action || '',
        resource: selectedPermission.resource || '',
      });
      setInitialLoadComplete(true);
    }
  }, [id, selectedPermission]);

  const validateField = useCallback((name, value) => {
    const errors = {};

    switch (name) {
      case 'name':
        if (!value) {
          errors.name = 'Le nom de la permission est requis';
        } else if (!/^[a-z]+:[a-z]+$/.test(value) && !/^[a-z]+:[a-z]+:[a-z]+$/.test(value)) {
          errors.name = 'Le nom de la permission doit suivre le format "module:action" ou "module:action:ressource"';
        }
        break;
      case 'module':
        if (!value) {
          errors.module = 'Le module est requis';
        }
        break;
      case 'action':
        if (!value) {
          errors.action = 'L\'action est requise';
        }
        break;
      default:
        break;
    }

    return errors;
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // Auto-generate name from module, action, and resource
    if ((name === 'module' || name === 'action' || name === 'resource') && formData.module && formData.action) {
      const module = name === 'module' ? value : formData.module;
      const action = name === 'action' ? value : formData.action;
      const resource = name === 'resource' ? value : formData.resource;
      
      const nameParts = [module, action];
      if (resource) {
        nameParts.push(resource);
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        name: nameParts.join(':'),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }

    if (touched[name]) {
      const fieldErrors = validateField(name, value);
      setFormErrors(prev => ({
        ...prev,
        ...fieldErrors,
      }));
    }
  }, [touched, validateField, formData]);

  const handleBlur = useCallback((e) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true,
    }));

    const fieldErrors = validateField(name, value);
    setFormErrors(prev => ({
      ...prev,
      ...fieldErrors,
    }));
  }, [validateField]);

  const handleActionSelect = useCallback((action) => {
    setFormData(prev => {
      const nameParts = [prev.module, action];
      if (prev.resource) {
        nameParts.push(prev.resource);
      }
      
      return {
        ...prev,
        action,
        name: nameParts.join(':'),
      };
    });
  }, []);

  const handleExampleClick = useCallback((example) => {
    setFormData({
      name: example.name,
      module: example.module,
      action: example.action,
      resource: example.resource || '',
      description: example.description || '',
    });
  }, []);

  const handleAddNewModule = useCallback(() => {
    if (newModule.trim()) {
      setFormData(prev => ({
        ...prev,
        module: newModule.trim(),
      }));
      setNewModule('');
      setShowNewModuleInput(false);
    }
  }, [newModule]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Validate all fields
    const allErrors = {};
    Object.keys(formData).forEach(key => {
      const fieldErrors = validateField(key, formData[key]);
      Object.assign(allErrors, fieldErrors);
    });

    if (Object.keys(allErrors).length > 0) {
      setFormErrors(allErrors);
      const allTouched = {};
      Object.keys(formData).forEach(key => {
        allTouched[key] = true;
      });
      setTouched(allTouched);
      return;
    }

    const submitData = {
      ...formData,
      resource: formData.resource || undefined,
    };

    try {
      if (id) {
        await dispatch(updatePermission(id, submitData));
      } else {
        await dispatch(createPermission(submitData));
      }
      navigate('/admin/permissions');
    } catch (error) {
      console.error('Form submission error:', error);
    }
  }, [dispatch, id, formData, validateField, navigate]);

  if ((loading) && !initialLoadComplete) {
    return (
      <div style={styles.loadingOverlay}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {id ? 'Modifier la permission' : 'Créer une nouvelle permission'}
        </h1>
        <p style={styles.subtitle}>
          {id ? 'Mettre à jour les détails de la permission' : 'Ajouter une nouvelle permission au système'}
        </p>
      </div>

      {error && <Alert type="error" message={error} onClose={() => dispatch(clearPermissionError())} />}
      {success && <Alert type="success" message={success} onClose={() => dispatch(clearPermissionSuccess())} />}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Permission Name */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>
            Nom de la permission <span style={styles.required}>*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="e.g., users:create or users:create:user"
            style={{
              ...styles.input,
              ...(touched.name && formErrors.name ? styles.inputError : {}),
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = touched.name && formErrors.name
                ? styles.inputErrorFocus.borderColor
                : styles.inputFocus.borderColor;
              e.currentTarget.style.boxShadow = touched.name && formErrors.name
                ? styles.inputErrorFocus.boxShadow
                : styles.inputFocus.boxShadow;
            }}
            onBlurCapture={(e) => {
              e.currentTarget.style.borderColor = touched.name && formErrors.name
                ? styles.inputError.borderColor
                : '#d1d5db';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          {touched.name && formErrors.name && (
            <div style={styles.errorMessage}>{formErrors.name}</div>
          )}
          <div style={styles.helperText}>
            Format: module:action or module:action:resource (e.g., users:create, users:create:user)
          </div>
        </div>

        {/* Module */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>
            Module <span style={styles.required}>*</span>
          </label>
          {!showNewModuleInput ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select
                name="module"
                value={formData.module}
                onChange={handleChange}
                onBlur={handleBlur}
                style={{
                  ...styles.select,
                  flex: 1,
                  ...(touched.module && formErrors.module ? styles.inputError : {}),
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = touched.module && formErrors.module
                    ? styles.inputErrorFocus.borderColor
                    : styles.inputFocus.borderColor;
                  e.currentTarget.style.boxShadow = touched.module && formErrors.module
                    ? styles.inputErrorFocus.boxShadow
                    : styles.inputFocus.boxShadow;
                }}
                onBlurCapture={(e) => {
                  e.currentTarget.style.borderColor = touched.module && formErrors.module
                    ? styles.inputError.borderColor
                    : '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <option value="">Select a module</option>
                {permissionModules?.map(module => (
                  <option key={module.name} value={module.name}>
                    {module.name} ({module.count})
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowNewModuleInput(true)}
                style={{
                  ...styles.actionButton,
                  padding: '0.625rem 1rem',
                  whiteSpace: 'nowrap',
                }}
              >
                + Nouveau
              </button>
            </div>
          ) : (
            <div style={styles.newModuleContainer}>
              <input
                type="text"
                value={newModule}
                onChange={(e) => setNewModule(e.target.value)}
                placeholder="Entrez le nom du nouveau module"
                style={styles.newModuleInput}
                autoFocus
              />
              <button
                type="button"
                onClick={handleAddNewModule}
                style={styles.addModuleButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.addModuleButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = styles.addModuleButton.backgroundColor;
                }}
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => setShowNewModuleInput(false)}
                style={styles.cancelModuleButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.cancelModuleButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = styles.cancelModuleButton.backgroundColor;
                }}
              >
                Cancel
              </button>
            </div>
          )}
          {touched.module && formErrors.module && (
            <div style={styles.errorMessage}>{formErrors.module}</div>
          )}
        </div>

        {/* Action */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>
            Action <span style={styles.required}>*</span>
          </label>
          <div style={styles.actionRow}>
            {commonActions.map(action => (
              <button
                key={action}
                type="button"
                style={{
                  ...styles.actionButton,
                  ...(formData.action === action ? styles.actionButtonSelected : {}),
                }}
                onMouseEnter={(e) => {
                  if (formData.action !== action) {
                    e.currentTarget.style.backgroundColor = styles.actionButtonHover.backgroundColor;
                  }
                }}
                onMouseLeave={(e) => {
                  if (formData.action !== action) {
                    e.currentTarget.style.backgroundColor = styles.actionButton.backgroundColor;
                  }
                }}
                onClick={() => handleActionSelect(action)}
              >
                {action}
              </button>
            ))}
          </div>
          <select
            name="action"
            value={formData.action}
            onChange={handleChange}
            onBlur={handleBlur}
            style={{
              ...styles.select,
              marginTop: '0.5rem',
              ...(touched.action && formErrors.action ? styles.inputError : {}),
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = touched.action && formErrors.action
                ? styles.inputErrorFocus.borderColor
                : styles.inputFocus.borderColor;
              e.currentTarget.style.boxShadow = touched.action && formErrors.action
                ? styles.inputErrorFocus.boxShadow
                : styles.inputFocus.boxShadow;
            }}
            onBlurCapture={(e) => {
              e.currentTarget.style.borderColor = touched.action && formErrors.action
                ? styles.inputError.borderColor
                : '#d1d5db';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <option value="">Ou laissez vide</option>
            {commonActions.map(action => (
              <option key={action} value={action}>{action}</option>
            ))}
          </select>
          {touched.action && formErrors.action && (
            <div style={styles.errorMessage}>{formErrors.action}</div>
          )}
        </div>

        {/* Resource (Optional) */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Resource (Optional)</label>
          <input
            type="text"
            name="resource"
            value={formData.resource}
            onChange={handleChange}
            placeholder="e.g., user, role, report (laissez vide pour un accès global)"
            style={styles.input}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = styles.inputFocus.borderColor;
              e.currentTarget.style.boxShadow = styles.inputFocus.boxShadow;
            }}
            onBlurCapture={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          <div style={styles.helperText}>
            Si spécifié, la permission s'applique uniquement à ce type de ressource. Laissez vide pour un accès global.
          </div>
        </div>

        {/* Description */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Décrivez ce que permet cette permission"
            style={styles.textarea}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = styles.inputFocus.borderColor;
              e.currentTarget.style.boxShadow = styles.inputFocus.boxShadow;
            }}
            onBlurCapture={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* Examples */}
        <div style={styles.examplesContainer}>
          <div style={styles.examplesTitle}>Exemples de permissions courantes</div>
          <div style={styles.examplesGrid}>
            {examplePermissions.map(example => (
              <div
                key={example.name}
                style={styles.exampleItem}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.exampleItemHover.backgroundColor;
                  e.currentTarget.style.borderColor = styles.exampleItemHover.borderColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
                onClick={() => handleExampleClick(example)}
              >
                <div style={styles.exampleName}>{example.name}</div>
                <div style={styles.exampleDesc}>{example.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Form Actions */}
        <div style={styles.formActions}>
          <button
            type="button"
            onClick={() => navigate('/admin/permissions')}
            style={styles.cancelButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
            }}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitButton,
              ...(loading ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {loading ? 'Sauvegarde en cours...' : id ? 'Mettre à jour la permission' : 'Créer une permission'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PermissionForm;